import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Users, AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminSubscriptions() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  useEffect(() => {
    checkAdminAccess();
    fetchSubscriptions();
  }, [statusFilter, tierFilter]);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!response.data.is_admin) {
        // Silently redirect non-admin users
        navigate('/');
      }
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/subscriptions?`;
      if (statusFilter) url += `status_filter=${statusFilter}&`;
      if (tierFilter) url += `tier_filter=${tierFilter}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, daysUntilExpiry) => {
    if (status === 'active' && daysUntilExpiry !== undefined && daysUntilExpiry <= 7) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
          <Clock className="w-4 h-4" />
          Expiring Soon ({daysUntilExpiry}d)
        </span>
      );
    }

    const styles = {
      active: 'bg-green-100 text-green-800 border-green-300',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      expired: 'bg-red-100 text-red-800 border-red-300',
      free: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    
    const icons = {
      active: <CheckCircle className="w-4 h-4" />,
      pending: <Clock className="w-4 h-4" />,
      expired: <XCircle className="w-4 h-4" />,
      free: <Users className="w-4 h-4" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.free}`}>
        {icons[status] || icons.free}
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Free'}
      </span>
    );
  };

  const getTierBadge = (tier) => {
    const styles = {
      plus: 'bg-blue-100 text-blue-800',
      pro: 'bg-purple-100 text-purple-800',
      free: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[tier] || styles.free}`}>
        {tier === 'plus' ? 'Teacher Plus' : tier === 'pro' ? 'Teacher Pro' : 'Free Forever'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter(s => s.subscription_status === 'active').length,
    expiringSoon: subscriptions.filter(s => s.days_until_expiry !== undefined && s.days_until_expiry <= 7 && s.days_until_expiry >= 0).length,
    expired: subscriptions.filter(s => s.subscription_status === 'expired').length,
    pending: subscriptions.filter(s => s.subscription_status === 'pending').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="py-8 px-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscriptions Overview</h1>
          <p className="text-gray-600">Monitor and manage all user subscriptions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Users</div>
          </div>
          <div className="bg-green-50 rounded-xl border border-green-200 p-4">
            <div className="text-2xl font-bold text-green-700">{stats.active}</div>
            <div className="text-sm text-green-600">Active</div>
          </div>
          <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.expiringSoon}</div>
            <div className="text-sm text-yellow-600">Expiring Soon</div>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-200 p-4">
            <div className="text-2xl font-bold text-red-700">{stats.expired}</div>
            <div className="text-sm text-red-600">Expired</div>
          </div>
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.pending}</div>
            <div className="text-sm text-blue-600">Pending</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex gap-4 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 outline-none"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
                <option value="free">Free</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tier</label>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 outline-none"
              >
                <option value="">All Tiers</option>
                <option value="plus">Teacher Plus</option>
                <option value="pro">Teacher Pro</option>
                <option value="free">Free Forever</option>
              </select>
            </div>
          </div>
        </div>

        {/* Subscriptions Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No subscriptions found</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Billing</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Expiry Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {subscriptions.map((sub) => (
                    <tr key={sub.user_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-semibold text-gray-900">{sub.name}</div>
                          <div className="text-sm text-gray-600">{sub.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getTierBadge(sub.subscription_tier)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900 capitalize">
                          {sub.billing_period || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(sub.subscription_status, sub.days_until_expiry)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {formatDate(sub.subscription_start_date)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {formatDate(sub.subscription_expiry_date)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {sub.days_until_expiry !== undefined ? (
                          <span className={`text-sm font-semibold ${
                            sub.days_until_expiry < 0 ? 'text-red-600' :
                            sub.days_until_expiry <= 3 ? 'text-orange-600' :
                            sub.days_until_expiry <= 7 ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {sub.days_until_expiry < 0 ? 'Expired' : `${sub.days_until_expiry} days`}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}

export default AdminSubscriptions;
